import { useState } from "react";
import { ArrowDownWideNarrow, Eye, Send, TrendingDown, X } from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { AnnouncementBody } from "@/features/whats_new/components/AnnouncementBody";
import { Countdown } from "@/features/rfq/components/Countdown";
import {
  useRunoffPreview,
  useStartRunoff,
  type GkOffer,
  type Proceeding,
  type RunoffMailPreview,
  type RunoffPayload,
} from "@/features/rfq/api/proceedings";

/**
 * Dogrywka po stronie kupca — ogłoszenie drugiej tury i jej przebieg.
 *
 * Dogrywka jest SPOKOJNA, nie licytacyjna: oferent może wyłącznie zejść do własnej
 * kwoty albo niżej i nie widzi ani cudzych ofert, ani swojej pozycji. Kupiec pisze
 * jeden komunikat do wszystkich i — jeśli chce — osobne dopytanie do konkretnej
 * firmy („proszę wyjaśnić pozycję montażową"), które trafia tylko do niej.
 *
 * Wysyłka jest DWUSTOPNIOWA, jak przy rozstrzygnięciu: najpierw konfiguracja, potem
 * osobny ekran z treścią każdego maila. Nie da się zaprosić do dogrywki, nie
 * zobaczywszy, co dokładnie i do kogo pójdzie.
 */

interface Props {
  proceeding: Proceeding;
  offers: GkOffer[];
  canManage: boolean;
}

const DEFAULT_HOURS = 24;

export function RunoffPanel({ proceeding, offers, canManage }: Props) {
  const active = offers.filter((o) => o.status === "submitted");
  const preview = useRunoffPreview(proceeding.id);
  const start = useStartRunoff(proceeding.id);

  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(String(DEFAULT_HOURS));
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [revealMax, setRevealMax] = useState(false);
  const [rows, setRows] = useState<RunoffMailPreview[] | null>(null);
  const [error, setError] = useState("");

  // Dogrywka już była — pokazujemy jej przebieg zamiast formularza.
  if (proceeding.has_runoff) {
    return <RunoffProgress proceeding={proceeding} offers={active} />;
  }
  // Przed terminem pierwszej tury nie ma czego dogrywać.
  if (proceeding.is_open_for_offers || proceeding.status !== "published") {
    return (
      <p className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4 text-sm text-[var(--color-text-secondary)]">
        Dogrywkę można ogłosić po terminie składania ofert — to druga, ostatnia
        tura, w której oferenci mogą już tylko obniżyć własną kwotę.
      </p>
    );
  }
  if (!canManage) return null;
  if (active.length === 0) {
    return (
      <p className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4 text-sm text-[var(--color-text-muted)]">
        Brak aktywnych ofert — nie ma kogo zaprosić do dogrywki.
      </p>
    );
  }

  const payload = (): RunoffPayload => ({
    hours: Number(hours) || 0,
    note,
    notes: Object.fromEntries(
      Object.entries(notes).filter(([, v]) => v.trim()),
    ),
    reveal_max_price: revealMax,
  });

  const detailOf = (err: unknown) =>
    (err as { response?: { data?: { errors?: { detail?: string } } } }).response
      ?.data?.errors?.detail;

  const hiddenMaxPrice =
    !!proceeding.max_price_net && !proceeding.max_price_visible;

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
      {!open ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="flex-1 text-sm text-[var(--color-text-secondary)]">
            Oferty są otwarte. Możesz dać oferentom{" "}
            <strong>spokojną dogrywkę</strong>: każdy zejdzie do własnej kwoty
            albo niżej, nie widząc cudzych ofert ani swojej pozycji. Do każdej
            firmy pójdzie osobny e-mail — wspólny komunikat, a jeśli chcesz,
            także indywidualne dopytanie.
          </p>
          <Button onClick={() => setOpen(true)}>
            <ArrowDownWideNarrow size={14} className="mr-1.5 inline" /> Ogłoś
            dogrywkę
          </Button>
        </div>
      ) : rows ? (
        <div className="space-y-3">
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            Ostatni krok — sprawdź, jakie zaproszenia pójdą i do kogo:
          </p>
          <RunoffMailList rows={rows} />
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRows(null)}>
              <X size={14} className="mr-1 inline" /> Wróć do edycji
            </Button>
            <Button
              disabled={start.isPending}
              onClick={() =>
                start.mutate(payload(), {
                  onSuccess: (meta) => {
                    setOpen(false);
                    setRows(null);
                    alert(
                      `Dogrywka ogłoszona. Zaproszono ${meta.invited} firm(y) — ` +
                        `termin: ${new Date(meta.deadline).toLocaleString("pl")}.`,
                    );
                  },
                  onError: (err) =>
                    setError(
                      detailOf(err) ?? "Nie udało się ogłosić dogrywki.",
                    ),
                })
              }
            >
              <Send size={14} className="mr-1.5 inline" /> Wyślij zaproszenia i
              otwórz dogrywkę
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                Czas trwania (godziny)
              </span>
              <input
                type="number"
                min={1}
                max={168}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-28 border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
              />
            </label>
            <p className="pb-2 text-xs text-[var(--color-text-muted)]">
              Domyślnie 24 h — tyle, żeby oferent zdążył policzyć na spokojnie,
              a postępowanie nie stanęło na tydzień.
            </p>
          </div>

          {hiddenMaxPrice && (
            <label className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={revealMax}
                onChange={(e) => setRevealMax(e.target.checked)}
              />
              <span>
                Odsłoń ukrytą cenę maksymalną (
                {formatAmount(proceeding.max_price_net!)} zł netto) uczestnikom
                dogrywki
                <span className="mt-0.5 block text-xs text-[var(--color-text-muted)]">
                  W drugiej turze oferent i tak może już tylko schodzić, więc
                  limit mówi mu wprost, poniżej czego trzeba zejść. Zobaczą go
                  wyłącznie firmy, które złożyły ofertę. Decyzji nie da się
                  cofnąć.
                </span>
              </span>
            </label>
          )}

          <label className="block">
            <span className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Komunikat wspólny — do wszystkich zaproszonych
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="np. Prosimy o ostateczną cenę. Zakres i termin bez zmian."
              className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
            />
          </label>

          <div>
            <p className="mb-1 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Dopytanie indywidualne (opcjonalnie) — widzi je tylko ta firma
            </p>
            <div className="space-y-2">
              {active.map((offer) => (
                <div
                  key={offer.id}
                  className="border-l-2 border-[var(--color-border)] pl-3"
                >
                  <p className="text-sm text-[var(--color-text-primary)]">
                    {offer.contractor_name}
                    {offer.amount_net !== null && (
                      <span className="ml-2 tabular-nums text-[var(--color-text-muted)]">
                        {formatAmount(offer.amount_net)} zł
                      </span>
                    )}
                  </p>
                  <textarea
                    value={notes[offer.contractor] ?? ""}
                    onChange={(e) =>
                      setNotes((prev) => ({
                        ...prev,
                        [offer.contractor]: e.target.value,
                      }))
                    }
                    rows={2}
                    placeholder="np. Prosimy o wyjaśnienie pozycji montażowej — cena odstaje od pozostałych."
                    className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
                  />
                </div>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                setError("");
              }}
            >
              <X size={14} className="mr-1 inline" /> Wróć
            </Button>
            <Button
              disabled={preview.isPending}
              onClick={() => {
                setError("");
                preview.mutate(payload(), {
                  onSuccess: setRows,
                  onError: (err) =>
                    setError(
                      detailOf(err) ?? "Nie udało się przygotować podglądu.",
                    ),
                });
              }}
            >
              <Eye size={14} className="mr-1.5 inline" /> Dalej — podgląd maili
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Przebieg ogłoszonej dogrywki: ile zostało czasu i kto już zszedł z ceny. */
function RunoffProgress({
  proceeding,
  offers,
}: {
  proceeding: Proceeding;
  offers: GkOffer[];
}) {
  const improved = offers.filter(
    (o) =>
      o.runoff_submitted_at &&
      o.amount_net !== null &&
      o.first_round_amount_net !== null &&
      Number(o.amount_net) < Number(o.first_round_amount_net),
  );
  return (
    <div className="rounded-lg border border-l-4 border-[var(--color-border)] border-l-[var(--color-primary)] bg-[var(--color-bg-surface)] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Badge color={proceeding.is_runoff_open ? "green" : "gray"}>
          {proceeding.is_runoff_open ? "Dogrywka trwa" : "Dogrywka zakończona"}
        </Badge>
        {proceeding.runoff_deadline && (
          <Countdown deadline={proceeding.runoff_deadline} />
        )}
        <span className="text-sm text-[var(--color-text-secondary)]">
          Poprawiło ofertę: <strong>{improved.length}</strong> z {offers.length}
        </span>
      </div>
      <p className="mt-2 text-xs text-[var(--color-text-muted)]">
        Ogłoszona{" "}
        {proceeding.runoff_started_at &&
          new Date(proceeding.runoff_started_at).toLocaleString("pl")}
        {proceeding.runoff_started_by_name &&
          ` · ${proceeding.runoff_started_by_name}`}
        {proceeding.max_price_revealed &&
          " · cena maksymalna odsłonięta uczestnikom"}
      </p>
      {proceeding.runoff_note && (
        <p className="mt-2 whitespace-pre-wrap border-t border-[var(--color-border)] pt-2 text-sm text-[var(--color-text-secondary)]">
          {proceeding.runoff_note}
        </p>
      )}
      {improved.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-[var(--color-border)] pt-2">
          {improved.map((o) => (
            <li
              key={o.id}
              className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-text-primary)]"
            >
              <TrendingDown
                size={13}
                className="text-green-600 dark:text-green-400"
              />
              {o.contractor_name}:{" "}
              <span className="tabular-nums text-[var(--color-text-muted)] line-through">
                {formatAmount(o.first_round_amount_net!)} zł
              </span>{" "}
              <span className="font-medium tabular-nums">
                {formatAmount(o.amount_net!)} zł
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RunoffMailList({ rows }: { rows: RunoffMailPreview[] }) {
  if (rows.length === 0)
    return (
      <p className="text-xs text-[var(--color-text-muted)]">
        Brak aktywnych ofert — nikt nie dostanie zaproszenia.
      </p>
    );
  return (
    <div className="space-y-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
      <p className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
        Zaproszenia, które pójdą ({rows.length}) — każda firma osobnym mailem
      </p>
      {rows.map((r) => (
        <div
          key={r.contractor_id}
          className="border-l-2 border-[var(--color-border)] pl-2 text-xs"
        >
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-[var(--color-text-primary)]">
              {r.contractor_name}
            </span>
            {r.personal_note && (
              <Badge color="blue">z dopytaniem indywidualnym</Badge>
            )}
            {r.mail_enabled ? (
              <span className="text-[var(--color-text-muted)]">
                {r.emails.length ? r.emails.join(", ") : "brak adresu"}
              </span>
            ) : (
              <span className="text-amber-600 dark:text-amber-500">
                mail wyłączony
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[var(--color-text-secondary)]">
            <span className="text-[var(--color-text-muted)]">Temat:</span>{" "}
            {r.subject}
          </p>
          {/* Treść renderowana markdownem — podgląd ma wyglądać tak, jak mail u
              odbiorcy, a nie sypać gwiazdkami. Ten sam renderer co przy zamówieniach. */}
          <AnnouncementBody text={r.body} className="mt-0.5 !text-xs" />
        </div>
      ))}
    </div>
  );
}
